import React, { useState } from "react";
import ReunionLevel5Picker from "./ReunionLevel5Picker";

const ReunionEventControl = ({ event, mode, onStart, onStop, onPause, onResume }) => {
  const [busy, setBusy] = useState(false);
  const live = event.isLive;
  const paused = event.isPaused;

  const handleStart = async () => {
    setBusy(true);
    await onStart();
    setBusy(false);
  };

  const handleStop = async () => {
    setBusy(true);
    await onStop();
    setBusy(false);
  };

  const handlePauseToggle = async () => {
    setBusy(true);
    if (paused) await onResume();
    else await onPause();
    setBusy(false);
  };

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-2xl font-black">
          {mode === "start" ? "▶️ Start Reunion" : "⏹ Stop Reunion"}
        </h2>
        <p className="text-sm text-emerald-200 mt-2">
          Control your live event with a single click.
        </p>
      </div>

      <div className="rounded-3xl bg-white/5 border border-white/10 backdrop-blur-md p-8">
        <div className="text-center">
          <div className="text-7xl mb-5">{live ? (paused ? "⏸" : "🟢") : "🟡"}</div>

          <div className="flex justify-center mb-3">
            <span
              className={`px-6 py-2 rounded-full text-xl lg:text-2xl font-black tracking-wide border ${
                !live
                  ? "bg-yellow-500/20 text-yellow-300 border-yellow-500/40"
                  : paused
                  ? "bg-orange-500/20 text-orange-300 border-orange-500/40"
                  : "bg-green-500/20 text-green-300 border-green-500/40"
              }`}
            >
              {!live ? "🟡 EVENT IS WAITING" : paused ? "⏸ EVENT PAUSED" : "🟢 EVENT IS LIVE"}
            </span>
          </div>
        </div>
      </div>

      {/* Level 5 picker — only meaningful before going live; picking mid-event
          would shift the pool under participants already at Level 5. */}
      {mode === "start" && !live && <ReunionLevel5Picker event={event} />}

      {/* Pause/Resume — only meaningful once live */}
      {live && (
        <div className="rounded-3xl bg-white/5 border border-white/10 p-6 text-center">
          <button
            onClick={handlePauseToggle}
            disabled={busy}
            className={`px-8 py-4 rounded-2xl font-bold text-lg disabled:opacity-40 ${
              paused
                ? "bg-gradient-to-r from-green-500 to-emerald-600"
                : "bg-gradient-to-r from-orange-500 to-amber-600"
            }`}
          >
            {busy ? "..." : paused ? "▶ Resume for Everyone" : "⏸ Pause for Everyone"}
          </button>
          <p className="text-xs text-emerald-300 mt-3">
            Freezes/resumes every participant's timer. Takes a few seconds to
            reach everyone (polling-based, not instant).
          </p>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
          <div className="text-4xl mb-3">👥</div>
          <div className="text-xs uppercase tracking-widest text-emerald-300">Joined</div>
          <div className="text-3xl font-black mt-2">{event.participantsJoined}</div>
        </div>
        <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
          <div className="text-4xl mb-3">🎟️</div>
          <div className="text-xs uppercase tracking-widest text-emerald-300">
            Max Participants
          </div>
          <div className="text-3xl font-black mt-2">{event.entryPassCount}</div>
        </div>
        <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
          <div className="text-4xl mb-3">🏆</div>
          <div className="text-xs uppercase tracking-widest text-emerald-300">Winners</div>
          <div className="text-3xl font-black mt-2">{event.winnerCount}</div>
        </div>
      </div>

      <div className="rounded-3xl bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-purple-500/10 border border-emerald-500/20 p-8">
        <div className="text-center">
          {mode === "start" ? (
            <>
              <div className="text-5xl mb-5">🚀</div>
              <h3 className="text-2xl font-black mb-4 text-purple-500">Ready to Go Live?</h3>
              <p className="text-emerald-200 max-w-2xl mx-auto mb-8">
                Once started, participants can begin answering questions
                immediately. Ensure everyone has joined and Level 5 questions
                are picked before starting.
              </p>
              <button
                onClick={handleStart}
                disabled={live || busy}
                className="px-8 py-4 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 text-lg font-bold disabled:opacity-40 hover:scale-105 transition-all"
              >
                {busy ? "Starting..." : "▶ START REUNION"}
              </button>
            </>
          ) : (
            <>
              <div className="text-5xl mb-5">🛑</div>
              <h3 className="text-2xl font-black mb-4 text-blue-400">End This Reunion?</h3>
              <p className="text-emerald-200 max-w-2xl mx-auto mb-8">
                This will stop new responses and prepare the event for winner
                announcement.
              </p>
              <button
                onClick={handleStop}
                disabled={!live || busy}
                className="px-8 py-4 rounded-2xl bg-gradient-to-r from-red-500 to-rose-600 text-lg font-bold disabled:opacity-40 hover:scale-105 transition-all"
              >
                {busy ? "Stopping..." : "⏹ STOP REUNION"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReunionEventControl;
